import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ICoupon } from '../../types/coupon';
import { couponService } from '../../services/couponService';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';

interface NotificationFormData {
  couponId?: string;
  triggerType: string;
  notificationText: string;
  notificationUrl?: string;
  notificationDescription: string;
  imageUrl: string;
  maxCount: number;
  isScheduledTask: boolean;
  scheduledTaskStartDate: Date | null;
  scheduledTaskStartTime: string;
  accountNumbers: string;
}

interface NotificationEditDialogProps {
  open: boolean;
  onClose: () => void;
  notification: ICoupon | null;
  onSaved: () => void;
}

const NotificationEditDialog: React.FC<NotificationEditDialogProps> = ({
  open,
  onClose,
  notification,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasRole } = useAuth();
  const canEditNotificationDetails = !hasRole(['notification:tester']);

  const getInitialFormData = (): NotificationFormData => {
    const now = new Date();
    let notificationText = notification?.notificationText || '';
    let notificationDescription = notification?.description || '';
    let accountNumbers = notification?.accountNumbers || '';

    // Auto-populate for tester role
    if (hasRole(['notification:tester'])) {
      accountNumbers = authService.getNotificationsAllowedAccounts()?.join('\n') || '';

      if (!notification) {
        const timeString = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()} sent ${now.getHours()}:${now.getMinutes()}`;
        notificationText = 'This is a Test Title ' + timeString;
        notificationDescription = 'This is a Test Description ' + timeString;
      }
    }

    return {
      couponId: notification?.couponId,
      triggerType: notification?.triggerType || 'assignment',
      notificationText,
      notificationUrl: notification?.notificationUrl || '',
      notificationDescription,
      imageUrl: notification?.imageUrl || '',
      maxCount: notification?.maxCount ?? 99999,
      isScheduledTask: notification?.isScheduledTask ? true : false,
      scheduledTaskStartDate: notification?.scheduledTaskStart
        ? new Date(notification.scheduledTaskStart)
        : null,
      scheduledTaskStartTime: notification?.scheduledTaskStart
        ? new Date(notification.scheduledTaskStart).toTimeString().substring(0, 5)
        : '00:00',
      accountNumbers,
    };
  };

  const [formData, setFormData] = useState<NotificationFormData>(getInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
      setErrors({});
      setImagePreview(null);
      setImageError(null);
    }
  }, [open, notification]);

  // Load and preview image when imageUrl changes
  useEffect(() => {
    const loadImage = async () => {
      if (!formData.imageUrl || !formData.imageUrl.match(/^https?:\/\/.+/)) {
        setImagePreview(null);
        setImageError(null);
        return;
      }

      setImageLoading(true);
      setImageError(null);
      setImagePreview(null);

      const tryLoadImage = (useCors: boolean): Promise<boolean> => {
        return new Promise((resolve) => {
          const img = new Image();
          if (useCors) {
            img.crossOrigin = 'anonymous';
          }

          const timeout = setTimeout(() => {
            resolve(false);
          }, 10000); // 10 second timeout

          img.onload = () => {
            clearTimeout(timeout);
            setImagePreview(formData.imageUrl);
            setImageError(null);
            resolve(true);
          };

          img.onerror = () => {
            clearTimeout(timeout);
            resolve(false);
          };

          img.src = formData.imageUrl;
        });
      };

      try {
        // First try without CORS (works for same-origin or servers that allow it)
        let loaded = await tryLoadImage(false);
        
        // If that fails, try with CORS
        if (!loaded) {
          loaded = await tryLoadImage(true);
        }

        // If both fail, it might be a CORS issue but the URL could still be valid
        if (!loaded) {
          // Don't set preview, but show a helpful message
          setImagePreview(null);
          setImageError('Unable to preview image (may be due to CORS restrictions). The URL will be saved and should work when the notification is used.');
        }
      } catch (error) {
        // Even on error, show a helpful message
        setImagePreview(null);
        setImageError('Unable to verify image. The URL will be saved and may work when the notification is used.');
      } finally {
        setImageLoading(false);
      }
    };

    // Debounce the image loading
    const timeoutId = setTimeout(() => {
      loadImage();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.imageUrl]);

  const handleChange = (field: keyof NotificationFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.notificationText) newErrors.notificationText = 'Please provide notification text';
    if (!formData.notificationDescription) {
      newErrors.notificationDescription = 'Please provide description';
    }
    if (formData.notificationUrl && !formData.notificationUrl.match(/^https?:\/\/.+/)) {
      newErrors.notificationUrl = 'Please provide valid Notification URL';
    }
    if (!formData.imageUrl || !formData.imageUrl.match(/^https?:\/\/.+/)) {
      newErrors.imageUrl = 'Please provide valid Image URL';
    }
    if (formData.maxCount < 0 || formData.maxCount > 99999) {
      newErrors.maxCount = 'Max count must be between 0 and 99999';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const combineDateAndTime = (date: Date | null, time: string): Date => {
    const combined = new Date(date || new Date());
    const [hours, minutes] = time.split(':');
    combined.setHours(parseInt(hours, 10));
    combined.setMinutes(parseInt(minutes, 10));
    return combined;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      let isScheduledTask = formData.isScheduledTask ? 1 : 0;
      let scheduledTaskStart: string | null = null;

      if (
        formData.triggerType === 'assignment' &&
        formData.isScheduledTask &&
        formData.scheduledTaskStartDate
      ) {
        scheduledTaskStart = combineDateAndTime(
          formData.scheduledTaskStartDate,
          formData.scheduledTaskStartTime
        ).toISOString();
      } else {
        isScheduledTask = 0;
      }

      await couponService.saveNotification(
        formData.couponId,
        'notification',
        formData.triggerType,
        formData.notificationText,
        formData.notificationUrl,
        formData.notificationDescription,
        formData.imageUrl,
        formData.maxCount,
        formData.accountNumbers,
        isScheduledTask,
        scheduledTaskStart
      );

      onSaved();
      onClose();
    } catch (error: any) {
      console.error('Error saving notification:', error);
      alert('Network error. Try again later');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '95vh', sm: '90vh' },
          width: { xs: '100%', sm: 'auto', md: '90%', lg: '85%' }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {notification ? 'Edit Notification' : 'Create Notification'}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 }, overflowY: 'auto' }}>
        <Box sx={{ pt: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            label="Notification Title"
            value={formData.notificationText}
            onChange={(e) => handleChange('notificationText', e.target.value)}
            error={!!errors.notificationText}
            helperText={errors.notificationText}
            required
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.notificationDescription}
            onChange={(e) => handleChange('notificationDescription', e.target.value)}
            error={!!errors.notificationDescription}
            helperText={errors.notificationDescription}
            inputProps={{ maxLength: 500 }}
            required
          />

          <TextField
            fullWidth
            label="Notification URL"
            value={formData.notificationUrl}
            onChange={(e) => handleChange('notificationUrl', e.target.value)}
            error={!!errors.notificationUrl}
            helperText={errors.notificationUrl}
          />

          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Image URL"
              value={formData.imageUrl}
              onChange={(e) => handleChange('imageUrl', e.target.value)}
              error={!!errors.imageUrl}
              helperText={errors.imageUrl}
              required
            />
            {imageLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading image...
                </Typography>
              </Box>
            )}
            {imageError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {imageError}
              </Alert>
            )}
            {imagePreview && !imageLoading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Image Preview:
                </Typography>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Notification preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: 'background.paper',
                  }}
                  onError={() => {
                    setImageError('Failed to display image');
                    setImagePreview(null);
                  }}
                />
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            select
            label="Trigger Type"
            value={formData.triggerType}
            onChange={(e) => handleChange('triggerType', e.target.value)}
            required
          >
            <MenuItem value="assignment">Selected Accounts</MenuItem>
            {canEditNotificationDetails && <MenuItem value="on_entry">On Entry</MenuItem>}
            {canEditNotificationDetails && <MenuItem value="on_exit">On Exit</MenuItem>}
          </TextField>

          {formData.triggerType === 'assignment' && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isScheduledTask}
                    onChange={(e) => handleChange('isScheduledTask', e.target.checked)}
                  />
                }
                label="Send at scheduled time"
              />

              {formData.isScheduledTask && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Scheduled Send Date"
                    value={
                      formData.scheduledTaskStartDate
                        ? formData.scheduledTaskStartDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      handleChange(
                        'scheduledTaskStartDate',
                        e.target.value ? new Date(e.target.value) : null
                      )
                    }
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    type="time"
                    label="Scheduled Send Time"
                    value={formData.scheduledTaskStartTime}
                    onChange={(e) => handleChange('scheduledTaskStartTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}
            </>
          )}

          <TextField
            fullWidth
            type="number"
            label="Maximum No of Sends"
            value={formData.maxCount}
            onChange={(e) => handleChange('maxCount', parseInt(e.target.value, 10))}
            error={!!errors.maxCount}
            helperText={errors.maxCount}
            inputProps={{ min: 0, max: 99999 }}
            required
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Account Numbers"
            value={formData.accountNumbers}
            onChange={(e) => handleChange('accountNumbers', e.target.value)}
            disabled={!canEditNotificationDetails}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Close
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary" 
          disabled={loading}
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationEditDialog;
